import { useState } from 'react'

import { API } from '@/api'
import { Button, Checkbox, Input, message, Modal, Trans } from '@/components'
import { PortProtocol } from '@/consts/machine'
import { Port } from '@/models/instance'

import { Instance } from '../../../models/instance'

const PORT_OP_TYPE = {
  ADD: 1,
  DELETE: 2,
}

export function PortModal(props: {
  open: boolean
  instance: Instance
  port?: Port
  mode: number
  onUpdate?: () => void
  onClose: () => void
}) {
  const { open, instance, port, mode, onClose } = props
  const [loading, setLoading] = useState(false)
  const [servicePort, setServicePort] = useState<number | undefined>(
    mode == 1 ? port?.srcPort : undefined,
  )
  const [protocol, setProtocol] = useState<PortProtocol>(
    mode == 1 ? port?.protocol || PortProtocol.SSH : PortProtocol.SSH,
  )
  // const [remark, setRemark] = useState<string | undefined>()

  const hasChanges = () => {
    if (!port) return true
    return servicePort !== port.destPort || protocol !== port.protocol
  }

  const onOK = () => {
    setLoading(true)
    const updateOperation =
      mode == 1
        ? [
            API.updateInstancePort({
              request_id: instance.displayID,
              type: PORT_OP_TYPE.DELETE,
              add_port_op: {
                ports: JSON.stringify([]),
              },
              remove_port_op: {
                ports: JSON.stringify([{ ...port }]),
              },
            }),
            API.updateInstancePort({
              request_id: instance.displayID,
              type: PORT_OP_TYPE.ADD,
              add_port_op: {
                ports: JSON.stringify([{ srcPort: servicePort, protocol }]),
              },
              remove_port_op: {
                ports: JSON.stringify([]),
              },
            }),
          ]
        : [
            API.updateInstancePort({
              request_id: instance.displayID,
              type: PORT_OP_TYPE.ADD,
              add_port_op: {
                ports: JSON.stringify([{ srcPort: servicePort, protocol }]),
              },
              remove_port_op: {
                ports: JSON.stringify([]),
              },
            }),
          ]

    Promise.all(updateOperation)
      .then((responses) => {
        if (responses.every((res) => res.code === 0)) {
          message.success('Task submitted')
          onClose && onClose()
        } else {
          message.error('Failed to update port')
        }
      })
      .catch((error) => {
        console.error('Error updating port:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Modal
      className="w-[640px]"
      open={open}
      onClose={() => {
        onClose && onClose()
      }}
    >
      <Modal.Header>
        {mode == 1 ? (
          <Trans i18nKey="instance.list.portModalTitleEdit">Edit Port Forwarding Rules</Trans>
        ) : (
          <Trans i18nKey="instance.list.portModalTitleNew">New Port Forwarding Rules</Trans>
        )}
      </Modal.Header>
      <Modal.Body>
        <>
          <>
            <h3 className="text-primary t-sb mb-2 flex">
              <Trans i18nKey="instance.list.portModalServicePort">Service Port</Trans>
              <div className="text-red-500">*</div>
            </h3>
            <Input
              type="number"
              value={servicePort}
              min={1}
              max={65535}
              disabled={loading}
              onChange={(e) => setServicePort(parseInt(e.target.value))}
            />
            <div className="mt-2 t-sm text-secondary">
              Your port must be a number between 1 to 65535.
            </div>
          </>
          <div className="mt-4">
            <h3 className="text-primary t-sb mb-2">
              <Trans i18nKey="instance.list.portModalProtocol">Protocol</Trans>
            </h3>
            <div className="flex gap-8">
              <h3 className="text-primary t-sb">
                <Trans i18nKey="instance.list.portModalTCP">TCP</Trans>
              </h3>
              <Checkbox
                checked={protocol === PortProtocol.HTTP}
                onChange={(checked) => {
                  checked ? setProtocol(PortProtocol.HTTP) : setProtocol(PortProtocol.SSH)
                }}
              >
                <h3 className="text-primary t-sm cursor-pointer">
                  <Trans i18nKey="instance.list.portModalHTTPS">HTTPS</Trans>
                </h3>
              </Checkbox>
            </div>
          </div>
          {/* <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="instance.list.portModalRemark">Remark</Trans>
            </h3>
            <Input
              type="text"
              value={remark}
              disabled={loading}
              placeholder="Please fill a remark"
              onChange={(e) => setRemark(e.target.value)}
            />
          </div> */}
        </>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            onClose && onClose()
          }}
        >
          <Trans i18nKey="instance.list.portModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" disabled={!servicePort || loading || !hasChanges()} onClick={onOK}>
          {mode == 1 ? (
            <Trans i18nKey="instance.list.portModalUpdate">Update</Trans>
          ) : (
            <Trans i18nKey="instance.list.portModalCreate">Create</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
